import React, { useEffect, useState, useRef, useCallback } from "react";
import { Row, Col, CardBody, Card, Alert, Container, Input, Label, Form, FormFeedback, Button, Spinner} from "reactstrap";

// Formik Validation
import * as Yup from "yup";
import { useFormik } from "formik";

import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// action
import { registerUser, resetRegisterFlag, googleLogin, microsoftLogin, githubLogin, resetLoginFlag } from "../../slices/thunks";

// Google Analytics tracking
import { trackRegisterAttempt, trackRegisterSuccess, trackRegisterError, trackOAuthRegister } from "../../helpers/analytics";

// OAuth hooks
import useGoogleAuth from "../../hooks/useGoogleAuth";
import useMicrosoftAuth from "../../hooks/useMicrosoftAuth";
import useGitHubAuth from "../../hooks/useGitHubAuth";

//redux
import { useSelector, useDispatch } from "react-redux";

import { Link, useNavigate } from "react-router-dom";

//import images
// Logo is now loaded from public folder
import ParticlesAuth from "../AuthenticationInner/ParticlesAuth";
import { createSelector } from "reselect";

const PASSWORD_RULE = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;

// Move selector outside component to maintain memoization
const selectLayoutState = (state:any) => state.Account;
const registerdatatype = createSelector(
    selectLayoutState,
    (account) => ({
        success: account.success,
        error: account.error,
        registrationError: account.registrationError,
    })
);

const Register = () => {
    const [loader, setLoader] = useState<boolean>(false);
    const history = useNavigate();
    const dispatch :any = useDispatch();

    const validation :any= useFormik({
        // enableReinitialize : use this flag when initial values needs to be changed
        enableReinitialize: true,

        initialValues: {
            email: '',
            fullName: '',
            company: '',
            password: '',
            confirmPassword: ''
        },
        validationSchema: Yup.object({
            email: Yup.string().email("Please enter a valid email address").required("Please enter your email"),
            fullName: Yup.string().required("Please enter your full name"),
            company: Yup.string().required("Please enter your company name"),
            password: Yup.string()
                .matches(PASSWORD_RULE, "Password must contain at least one uppercase letter, one lowercase letter, and one number, and must be a minimum of 8 characters long.")
                .required("Please enter your password"),
            confirmPassword: Yup.string()
                .oneOf([Yup.ref("password")], "Passwords do not match")
                .required("Please confirm your password"),
        }),
        onSubmit: (values) => {
            // Track registration attempt (GDPR/CCPA compliant - no PII)
            trackRegisterAttempt();

            dispatch(registerUser({
                email: values.email,
                password: values.password,
                fullName: values.fullName,
                company: values.company,
            }));
            setLoader(true)
        }
    });

    const {
        error, success, registrationError
    } = useSelector(registerdatatype);

    // Google OAuth configuration
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
    const [googleButtonContainer, setGoogleButtonContainer] = useState<HTMLDivElement | null>(null);
    const googleButtonRendered = useRef(false);

    // Memoize callbacks to prevent infinite re-renders
    const handleGoogleSuccess = useCallback((response: any) => {
        console.log('Google login success:', response);

        // Track OAuth registration
        trackOAuthRegister('google');

        setLoader(true);
        dispatch(googleLogin(response.credential, history));
    }, [dispatch, history]);

    const handleGoogleError = useCallback((error: any) => {
        console.error('Google login error:', error);
        dispatch(resetLoginFlag());
        setLoader(false);
    }, [dispatch]);

    const { isLoaded: isGoogleLoaded, renderButton, error: googleError } = useGoogleAuth({
        clientId,
        onSuccess: handleGoogleSuccess,
        onError: handleGoogleError,
        autoSelect: false,
    });

    // Microsoft OAuth configuration
    const microsoftClientId = process.env.REACT_APP_MICROSOFT_CLIENT_ID || '';

    const handleMicrosoftSuccess = useCallback((response: any) => {
        console.log('Microsoft login success:', response);

        // Track OAuth registration (GDPR/CCPA compliant - no PII)
        trackOAuthRegister('microsoft');

        setLoader(true);
        dispatch(microsoftLogin(response, history));
    }, [dispatch, history]);

    const handleMicrosoftError = useCallback((error: any) => {
        console.error('Microsoft login error:', error);
        dispatch(resetLoginFlag());
        setLoader(false);
    }, [dispatch]);

    const {
        isLoaded: isMicrosoftLoaded,
        signIn: microsoftSignIn,
        error: microsoftError
    } = useMicrosoftAuth({
        clientId: microsoftClientId,
        onSuccess: handleMicrosoftSuccess,
        onError: handleMicrosoftError,
    });

    // GitHub OAuth configuration
    const githubClientId = process.env.REACT_APP_GITHUB_CLIENT_ID || '';

    const handleGitHubSuccess = useCallback((response: any) => {
        console.log('GitHub login success:', response);

        // Track OAuth registration
        trackOAuthRegister('github');

        setLoader(true);
        dispatch(githubLogin(response, history));
    }, [dispatch, history]);

    const handleGitHubError = useCallback((error: any) => {
        console.error('GitHub login error:', error);
        dispatch(resetLoginFlag());
        setLoader(false);
    }, [dispatch]);

    const {
        isLoaded: isGitHubLoaded,
        signIn: githubSignIn,
        error: githubError
    } = useGitHubAuth({
        clientId: githubClientId,
        onSuccess: handleGitHubSuccess,
        onError: handleGitHubError,
    });

    // Render Google button when container is ready and Google is loaded
    useEffect(() => {
        if (googleButtonContainer && isGoogleLoaded && !googleError && !googleButtonRendered.current) {
            renderButton(googleButtonContainer, {
                theme: 'outline',
                size: 'large',
                text: 'signup_with',
                shape: 'rectangular',
                width: '100%',
            });
            googleButtonRendered.current = true;
        }
    }, [googleButtonContainer, isGoogleLoaded, renderButton, googleError]);

    useEffect(() => {
        if (success) {
            // Track successful registration (GDPR/CCPA compliant - no PII)
            trackRegisterSuccess();

            setTimeout(() => history("/login"), 3000);
        }

        if (error) {
            // Track registration error (GDPR/CCPA compliant - no PII)
            trackRegisterError(
                registrationError?.message || 'Registration failed'
            );
        }

        if (success || error) {
            setLoader(false);
        }

        const timer = setTimeout(() => {
            dispatch(resetRegisterFlag());
        }, 3000);

        return () => clearTimeout(timer);

    }, [dispatch, success, error, history, validation.values, registrationError]);

    document.title = "Basic SignUp | Velzon - React Admin & Dashboard Template";

    return (
        <React.Fragment>
            {/*<ParticlesAuth>*/}
                <div className="auth-page-content" style={{ backgroundColor: '#ffffff', minHeight: '100vh' }}>
                    <Container>
                        <Row>
                            <Col lg={12}>
                                <div className="text-center mt-sm-3 mb-3">
                                    <div>
                                        <Link to="/" className="d-inline-block auth-logo">
                                            <div className="d-flex align-items-center gap-2">
                                                <i className="ri-sparkling-line" style={{ fontSize: '40px', color: '#7F13EC' }}></i>
                                                <span style={{ fontSize: '24px', fontWeight: 'bold', color: '#7F13EC' }}>Integr.Dev</span>
                                            </div>
                                        </Link>
                                    </div>
                                    {/*<p className="mt-3 fs-15 fw-medium">Integr Dev Portal</p>*/}
                                </div>
                            </Col>
                        </Row>

                        <Row className="justify-content-center">
                            <Col md={10} lg={9} xl={8}>
                                <Card className="mt-3" style={{ border: '1px solid #e0e0e0', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>

                                    <CardBody className="p-4">
                                        <div className="text-center mt-2">
                                            <h5 className="text-primary">Create New Account</h5>
                                            <p className="text-muted">Join Integr Dev to get started</p>
                                        </div>
                                        <div className="p-2 mt-4">
                                            <Form
                                                onSubmit={(e:any) => {
                                                    e.preventDefault();
                                                    validation.handleSubmit();
                                                    return false;
                                                }}
                                                className="needs-validation" action="#">

                                                {success && success ? (
                                                    <>
                                                        {toast("Your Redirect To Login Page...", { position: "top-right", hideProgressBar: false, className: 'bg-success text-white', progress: undefined, toastId: "" })}
                                                        <ToastContainer autoClose={2000} limit={1} />
                                                        <Alert color="success">
                                                            Register User Successfully and Your Redirect To Login Page...
                                                        </Alert>
                                                    </>
                                                ) : null}

                                                {error ? (
                                                    <Alert color="danger">
                                                        <div>{registrationError?.message || 'Unable to register. Please try again.'}</div>
                                                    </Alert>
                                                ) : null}

                                                {/* Row 1: Full Name and Company Name */}
                                                <Row>
                                                    <Col md={6}>
                                                        <div className="mb-3">
                                                            <Label htmlFor="fullName" className="form-label">Full Name <span className="text-danger">*</span></Label>
                                                            <Input
                                                                id="fullName"
                                                                name="fullName"
                                                                type="text"
                                                                placeholder="Enter your full name"
                                                                onChange={validation.handleChange}
                                                                onBlur={validation.handleBlur}
                                                                value={validation.values.fullName || ""}
                                                                invalid={
                                                                    validation.touched.fullName && validation.errors.fullName ? true : false
                                                                }
                                                            />
                                                            {validation.touched.fullName && validation.errors.fullName ? (
                                                                <FormFeedback type="invalid"><div>{validation.errors.fullName}</div></FormFeedback>
                                                            ) : null}
                                                        </div>
                                                    </Col>
                                                    <Col md={6}>
                                                        <div className="mb-3">
                                                            <Label htmlFor="company" className="form-label">Company Name <span className="text-danger">*</span></Label>
                                                            <Input
                                                                id="company"
                                                                name="company"
                                                                type="text"
                                                                placeholder="Enter your company name"
                                                                onChange={validation.handleChange}
                                                                onBlur={validation.handleBlur}
                                                                value={validation.values.company || ""}
                                                                invalid={
                                                                    validation.touched.company && validation.errors.company ? true : false
                                                                }
                                                            />
                                                            {validation.touched.company && validation.errors.company ? (
                                                                <FormFeedback type="invalid"><div>{validation.errors.company}</div></FormFeedback>
                                                            ) : null}
                                                        </div>
                                                    </Col>
                                                </Row>

                                                {/* Row 2: Email (full width) */}
                                                <div className="mb-3">
                                                    <Label htmlFor="useremail" className="form-label">Email <span className="text-danger">*</span></Label>
                                                    <Input
                                                        id="email"
                                                        name="email"
                                                        className="form-control"
                                                        placeholder="Enter email address"
                                                        type="email"
                                                        onChange={validation.handleChange}
                                                        onBlur={validation.handleBlur}
                                                        value={validation.values.email || ""}
                                                        invalid={
                                                            validation.touched.email && validation.errors.email ? true : false
                                                        }
                                                    />
                                                    {validation.touched.email && validation.errors.email ? (
                                                        <FormFeedback type="invalid"><div>{validation.errors.email}</div></FormFeedback>
                                                    ) : null}
                                                </div>

                                                {/* Row 3: Password and Confirm Password */}
                                                <Row>
                                                    <Col md={6}>
                                                        <div className="mb-3">
                                                            <Label htmlFor="userpassword" className="form-label">Password <span className="text-danger">*</span></Label>
                                                            <Input
                                                                name="password"
                                                                type="password"
                                                                placeholder="Enter Password"
                                                                onChange={validation.handleChange}
                                                                onBlur={validation.handleBlur}
                                                                value={validation.values.password || ""}
                                                                invalid={
                                                                    validation.touched.password && validation.errors.password ? true : false
                                                                }
                                                            />
                                                            {validation.touched.password && validation.errors.password ? (
                                                                <FormFeedback type="invalid"><div>{validation.errors.password}</div></FormFeedback>
                                                            ) : null}
                                                        </div>
                                                    </Col>
                                                    <Col md={6}>
                                                        <div className="mb-3">
                                                            <Label htmlFor="confirmPassword" className="form-label">Confirm Password <span className="text-danger">*</span></Label>
                                                            <Input
                                                                name="confirmPassword"
                                                                type="password"
                                                                placeholder="Confirm Password"
                                                                onChange={validation.handleChange}
                                                                onBlur={validation.handleBlur}
                                                                value={validation.values.confirmPassword || ""}
                                                                invalid={
                                                                    validation.touched.confirmPassword && validation.errors.confirmPassword ? true : false
                                                                }
                                                            />
                                                            {validation.touched.confirmPassword && validation.errors.confirmPassword ? (
                                                                <FormFeedback type="invalid"><div>{validation.errors.confirmPassword}</div></FormFeedback>
                                                            ) : null}
                                                        </div>
                                                    </Col>
                                                </Row>

                                                {/* Password hint */}
                                                <div className="mb-3">
                                                    <small className="text-muted">
                                                        <i className="ri-information-line me-1"></i>
                                                        Password must contain at least one uppercase letter, one lowercase letter, one number, and be at least 8 characters long.
                                                    </small>
                                                </div>

                                                <div className="mb-4">
                                                    <p className="mb-0 fs-12 text-muted fst-italic">By registering you agree to the Integr Dev
                                                        <Link to="#" className="text-primary text-decoration-underline fst-normal fw-medium ms-1">Terms of Use</Link></p>
                                                </div>

                                                <div className="mt-4">
                                                <Button color="success" className="w-100" type="submit" disabled={loader}>
                                                        {loader && <Spinner size="sm" className='me-2'> Loading... </Spinner>}
                                                        Sign Up
                                                    </Button>
                                                </div>

                                                <div className="mt-4 text-center">
                                                    <div className="signin-other-title">
                                                        <h5 className="fs-13 mb-4 title">Or sign up with</h5>
                                                    </div>

                                                    {/* OAuth Buttons in One Row */}
                                                    <Row>
                                                        {/* Google Sign-In Button */}
                                                        <Col md={4}>
                                                            <div className="mb-3">
                                                                {googleError ? (
                                                                    <Alert color="warning" className="mb-3">
                                                                        <small>{googleError}</small>
                                                                    </Alert>
                                                                ) : (
                                                                    <div
                                                                        ref={setGoogleButtonContainer}
                                                                        style={{ minHeight: '44px' }}
                                                                    >
                                                                        {!isGoogleLoaded && (
                                                                            <Button color="light" disabled className="w-100">
                                                                                <Spinner size="sm" className="me-2" />
                                                                                Loading...
                                                                            </Button>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </Col>

                                                        {/* Microsoft Sign-Up Button */}
                                                        <Col md={4}>
                                                            <div className="mb-3">
                                                                <Button
                                                                    color="primary"
                                                                    className="w-100 d-flex align-items-center justify-content-center gap-2"
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        if (isMicrosoftLoaded && !loader) {
                                                                            microsoftSignIn();
                                                                        }
                                                                    }}
                                                                    disabled={!isMicrosoftLoaded || loader}
                                                                    title="Sign up with Microsoft"
                                                                >
                                                                    {!isMicrosoftLoaded ? (
                                                                        <>
                                                                            <Spinner size="sm" />
                                                                            <span className="d-none d-lg-inline">Loading...</span>
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <i className="ri-microsoft-fill fs-16"></i>
                                                                            <span className="d-none d-lg-inline">Sign up with Microsoft</span>
                                                                        </>
                                                                    )}
                                                                </Button>
                                                            </div>
                                                        </Col>

                                                        {/* GitHub Sign-Up Button */}
                                                        <Col md={4}>
                                                            <div className="mb-3">
                                                                <Button
                                                                    color="dark"
                                                                    className="w-100 d-flex align-items-center justify-content-center gap-2"
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        if (isGitHubLoaded && !loader) {
                                                                            githubSignIn();
                                                                        }
                                                                    }}
                                                                    disabled={!isGitHubLoaded || loader}
                                                                    title="Sign up with GitHub"
                                                                >
                                                                    {!isGitHubLoaded ? (
                                                                        <>
                                                                            <Spinner size="sm" />
                                                                            <span className="d-none d-lg-inline">Loading...</span>
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <i className="ri-github-fill fs-16"></i>
                                                                            <span className="d-none d-lg-inline">Sign up with GitHub</span>
                                                                        </>
                                                                    )}
                                                                </Button>
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                </div>
                                            </Form>
                                        </div>
                                    </CardBody>
                                </Card>
                                <div className="mt-4 text-center">
                                    <p className="mb-0">Already have an account ? <Link to="/login" className="fw-semibold text-primary text-decoration-underline"> Signin </Link> </p>
                                </div>
                            </Col>
                        </Row>

                        {/* Copyright */}
                        <Row>
                            <Col lg={12}>
                                <div className="text-center mt-3 mb-3">
                                    <p className="mb-0 text-muted">© {new Date().getFullYear()} Integr Dev. All rights reserved.</p>
                                </div>
                            </Col>
                        </Row>
                    </Container>
                </div>
            {/*</ParticlesAuth>*/}
        </React.Fragment>
    );
};

export default Register;
